import { useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useSnackbar } from "notistack";
import buildHubUrl from "./buildHubUrl";
import CreateConnection from "./HubMatrix";
import { connectToHubCashDrawer } from "src/redux/slices/hub";
import { upsertTx, setDeviceStatus } from "src/redux/slices/cashdrawer";

// Hook que se suscribe al hub `cashdrawer` (grupo `restaurante-{id}`).
// Eventos:
//   - `cashdrawer-update`         → snapshot de una Tx (pending → …
//     terminal). upsert en pendingTx.
//   - `cashdrawer-device-status`  → status de conectividad del hardware
//     reportado por el Worker (cada 30s + ante peticiones puntuales).
//     Guardamos en `state.cashdrawer.deviceStatus` para pintar chip Y
//     además lanzamos snackbar cuando cambia reachable ↔ unreachable
//     (sólo en transición, para no spamear al camarero cada 30s).
export default function useCashDrawerHub() {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useSelector((s) => s.authJwt);
  const restauranteId = user?.id;
  // Ref para detectar transiciones sin re-renderizar. `undefined` = todavía
  // no hemos recibido ningún status (silencio la primera vez, evita
  // "toast al abrir la app" cuando el cajón ya estaba caído).
  const lastReachableRef = useRef(undefined);

  useEffect(() => {
    if (!restauranteId) return undefined;

    const cleanup = CreateConnection(
      buildHubUrl("/hub/cashdrawer", { restauranteId }),
      [
        {
          id: "cashdrawer-update",
          operator: (snapshot) => {
            if (!snapshot) return;
            dispatch(upsertTx(snapshot));
          },
        },
        {
          id: "cashdrawer-device-status",
          operator: (status) => {
            if (!status) return;
            dispatch(setDeviceStatus(status));

            // Alerta visible al camarero. Reglas:
            //  - Solo si el provider soporta probe (Supported=true) —
            //    sin IP configurada no tiene sentido notificar nada.
            //  - Solo en TRANSICIÓN — no cada 30s.
            //  - Primera medición desde carga se silencia (undefined
            //    → cualquier estado) para no aparecer al abrir el TPV
            //    con el cajón ya caído.
            if (!status.supported) {
              lastReachableRef.current = undefined;
              return;
            }
            const prev = lastReachableRef.current;
            lastReachableRef.current = status.reachable;
            if (prev === undefined) return;
            if (prev === status.reachable) return;

            if (status.reachable) {
              enqueueSnackbar(
                `Cajón automático recuperado${status.latencyMs ? ` · ${status.latencyMs}ms` : ""}`,
                { variant: "success", autoHideDuration: 4000 },
              );
            } else {
              enqueueSnackbar(
                `El cajón automático no responde. ${status.message || ""}`.trim(),
                { variant: "warning", autoHideDuration: 8000 },
              );
            }
          },
        },
      ],
      dispatch,
      connectToHubCashDrawer,
      "cashDrawer",
    );
    return cleanup;
  }, [restauranteId, dispatch, enqueueSnackbar]);
}
